import { useState } from "react";
import { Link, useNavigate, useRouteLoaderData, useSearchParams } from "react-router-dom";

import { useUiStore } from "src/components/main/uiStore";
import {
    getCurrentWeekRange,
    myAxios,
    CCTVReportWriterEmails,
    potholingReportWriterEmails,
} from "src/components/common/functions";
import type { ReportConflict, ReportRecord } from "./types";
import { AsyncFormSelect, SelectOption } from "../common/AsyncSelect";

const title = "Reports";

type ReportLoaderData = {
    report: ReportRecord;
    start: string;
    end: string;
};

export default function ReportForm() {
    const reportData = useRouteLoaderData("Report") as ReportLoaderData | undefined;
    const report = reportData?.report;
    const id = report?.id;
    const keyword = report ? "Update" : "Add";
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const setShowLoader = useUiStore(state => state.setShowLoader);
    const [stats, setStats] = useState<{ p?: number; ep?: number; manhole?: number } | "">("");
    const [curProjectId, setProjectId] = useState(report?.projectId ?? "");
    const [curProjectName, setProjectName] = useState(report?.projectName ?? "");
    const [curStartDate, setStartDate] = useState(report?.startDate ?? "");
    const [curEndDate, setEndDate] = useState(report?.endDate ?? "");
    const [curReportTechId, setReportTechId] = useState(report?.reportTechId ?? "");
    const [curReportTechName, setReportTechName] = useState(report?.reportTech ?? "");
    const [curSup, setSup] = useState(report?.sup ?? "");
    const [invalidText, setInvalidText] = useState<ReportConflict | "">("");
    const currentWeekRange = getCurrentWeekRange();
    const effectiveStart = reportData?.start ?? searchParams.get("start") ?? currentWeekRange.start;
    const effectiveEnd = reportData?.end ?? searchParams.get("end") ?? currentWeekRange.end;

    const appendSharedFields = (formData: FormData) => {
        formData.append("projectName", curProjectName);
        formData.append("start", effectiveStart);
        formData.append("end", effectiveEnd);
    };

    const updateReport = async (formData: FormData) => {
        setShowLoader(true);
        const res = await myAxios({ fileName: "reports/updateReport.php", formData });
        setShowLoader(false);
        if (!res) return;
        navigate(`/${title}`);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        if (!formData.get("projectId")) {
            window.alert("Please select a project.");
            return;
        }
        appendSharedFields(formData);
        await updateReport(formData);
    };

    return (
        <form className="my-3" onSubmit={handleSubmit}>
            <input className="nodisplay" readOnly name="action" value={keyword} />
            <input className="nodisplay" readOnly name="id" value={id || ""} />
            <input className="nodisplay" readOnly name="pdfId" value={report?.pdfId || ""} />
            <div className="my-3">
                <label className="inline fw-bold me-3 required-field">Project Name:</label>
                <AsyncFormSelect
                    className="flex-grow-1"
                    placeholder="Search projects…"
                    required={true}
                    name="projectId"
                    inputId="projectId"
                    fileName="common/searchProject.php"
                    value={curProjectId ? { value: curProjectId, label: curProjectName } : null}
                    onChange={next => {
                        setStartDate("");
                        setEndDate("");
                        setInvalidText("");
                        setProjectId((next as SelectOption)?.value ?? "");
                        setProjectName((next as SelectOption)?.label ?? "");
                    }}
                />
                <input className="nodisplay" readOnly name="projectId" value={curProjectId} />
            </div>
            <div className="my-3">
                <label className="inline fw-bold me-3 required-field" htmlFor="startDate">Start Date:</label>
                <input
                    id="startDate"
                    type="date"
                    required
                    className="form-control me-3"
                    name="startDate"
                    value={curStartDate}
                    onChange={e => {
                        setStartDate(e.currentTarget.value);
                        setInvalidText("");
                    }}
                />
            </div>
            <div className="my-3">
                <label className="inline fw-bold me-3 required-field" htmlFor="endDate">End Date:</label>
                <input
                    id="endDate"
                    type="date"
                    required
                    className="form-control"
                    name="endDate"
                    value={curEndDate}
                    onChange={e => {
                        setEndDate(e.currentTarget.value);
                        setInvalidText("");
                    }}
                />
            </div>
            {invalidText ? (
                <div className="my-3 mx-3 text-danger fw-bold">
                    <span className="me-1">conflict with</span>
                    <Link to={`/${title}/${invalidText.id}`}>
                        from {invalidText.startDate} to {invalidText.endDate}
                    </Link>
                </div>
            ) : null}
            <div className="my-3">
                <label className="inline me-3 fw-bold" htmlFor="reportTechId">Report Tech</label>
                <AsyncFormSelect
                    className="flex-grow-1"
                    placeholder="Search report tech..."
                    required={true}
                    name="reportTechId"
                    inputId="reportTechId"
                    fileName="reports/searchReportTech.php"
                    value={curReportTechId ? { value: curReportTechId, label: curReportTechName } : null}
                    onChange={next => {
                        setReportTechId((next as SelectOption)?.value ?? "");
                        setReportTechName((next as SelectOption)?.label ?? "");
                    }}
                />
            </div>
            <div className="my-3">
                <label className="inline me-3 fw-bold" htmlFor="code">Code</label>
                <input type="text" name="code" id="code" defaultValue={report?.code} className="form-control me-3" />
            </div>
            <div className="my-3">
                <label className="inline me-3 fw-bold" htmlFor="priority">Priority</label>
                <select name="priority" id="priority" defaultValue={report?.priority} className="form-control">
                    <option value="">Select</option>
                    <option>Low</option>
                    <option>Medium</option>
                    <option>High</option>
                </select>
            </div>
            {(potholingReportWriterEmails as string[]).includes(curReportTechId) ? (
                <div className="my-3">
                    <label className="inline me-3 fw-bold" htmlFor="pothole"># of Potholes</label>
                    <input type="number" name="pothole" id="pothole" defaultValue={report?.pothole} className="form-control" />
                    <label className="inline mx-3 fw-bold" htmlFor="ep"># of Electric Potholes</label>
                    <input type="number" name="ep" id="ep" defaultValue={report?.ep} className="form-control" />
                    <label className="inline mx-3 fw-bold" htmlFor="manhole"># of Manholes</label>
                    <input type="number" name="manhole" id="manhole" defaultValue={report?.manhole} className="form-control" />
                </div>
            ) : null}
            {(potholingReportWriterEmails as string[]).includes(curReportTechId) && curProjectId && curStartDate && curEndDate ? (
                <div>
                    <span
                        className="btn btn-info"
                        onClick={async () => {
                            const formData = new FormData();
                            formData.append("projectId", curProjectId);
                            formData.append("startDate", curStartDate);
                            formData.append("endDate", curEndDate);
                            setShowLoader(true);
                            const res = await myAxios({ fileName: "reports/readStats.php", formData });
                            setShowLoader(false);
                            if (!res) return;
                            if (!res.data.p.length && !res.data.ep.length && !res.data.manhole.length) {
                                window.alert("No Data Sheets found.");
                            }
                            setStats(res.data as { p?: number; ep?: number; manhole?: number });
                        }}
                    >
                        Get Stats
                    </span>
                    {stats ? <span className="ms-3">{[stats.p ? `${stats.p} Potholes` : "", stats.ep ? `${stats.ep} Electric Potholes` : "", stats.manhole ? `${stats.manhole} Manholes` : ""].filter(Boolean).join(",  ")}</span> : null}
                </div>
            ) : null}
            <div className="my-3">
                <label className="inline fw-bold me-3" htmlFor="sup">Supplementary Report</label>
                <select name="sup" id="sup" value={curSup} onChange={e => setSup(e.currentTarget.value)} className="form-control">
                    <option value="">No</option>
                    <option>Yes</option>
                </select>
            </div>
            <div className="my-3">
                <label className="inline me-3 fw-bold" htmlFor="pending">Pending on Other Department</label>
                <select name="pending" id="pending" defaultValue={report?.pending} className="form-control">
                    <option value="">No</option>
                    <option>Yes</option>
                </select>
            </div>
            <div className="my-3">
                <label className="h4" htmlFor="notes">Notes:</label>
                <textarea id="notes" name="notes" defaultValue={report?.notes || ""} className="form-control mb-3" style={{ width: "100%" }} />
            </div>
            {(CCTVReportWriterEmails as string[]).includes(curReportTechId) ? (
                <div className="my-3">
                    <label className="my-3 h4">CAD Local Path:</label>
                    <textarea name="cadLocation" defaultValue={report?.cadLocation || ""} className="form-control mb-3" style={{ width: "100%" }} />
                    <label className="my-3 h4">Video Local Path:</label>
                    <textarea name="videoLocation" defaultValue={report?.videoLocation || ""} className="form-control mb-3" style={{ width: "100%" }} />
                </div>
            ) : null}
            <div>
                <button
                    children={keyword}
                    className="btn btn-primary me-3"
                />
                <Link children="Back" to=".." className="btn btn-outline-secondary float-end" />
            </div>
        </form>
    );
}
